import dayjs, { Dayjs } from "dayjs";
import localeData from "dayjs/plugin/localeData";
import LocalizedFormat from "dayjs/plugin/localizedFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { Form, Formik, FormikProps } from "formik";
import { useMemo, useState, useEffect, forwardRef } from "react";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import "dayjs/locale/en-ie";
import {
  ActionIntervalType,
  IIntervalOption,
  IProtocolActions,
} from "../../../../../../@types/Protocol";
import { Close } from "../../../../../../assets/icons/Close";
import { DateTimePickerInput } from "../../../../../../components/DateTimePicker";
import {
  IOption,
  SelectDropdown,
} from "../../../../../../components/SelectDropdown";
import { TextField } from "../../../../../../components/TextField";
import { IOptionGroup } from "../../ProtocolBuilder";
import {
  ActiveBar,
  CloseContainer,
  ErrorMessage,
  Section,
  SectionItem,
  selectCss,
  textFieldClassName,
} from "./ActionSection.styles";

dayjs.extend(LocalizedFormat);
dayjs.extend(localeData);
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * The recurring intervals — the ones that take an iteration count ("every N days"). The value is
 * the key for that field's label, which used to be derived by slicing "Every " off the server's
 * display name. Keyed on the API's ActionIntervalType so a translated display name cannot make
 * the field, or the validation that depends on it, silently disappear (EN-5191).
 */
const ITERATION_LABEL_KEYS: Partial<Record<ActionIntervalType, string>> = {
  EveryXHours: "protocols_action_iterationLabel_hours",
  EveryXDays: "protocols_action_iterationLabel_days",
  EveryXWeeks: "protocols_action_iterationLabel_weeks",
  EveryXMonths: "protocols_action_iterationLabel_months",
  EveryXYears: "protocols_action_iterationLabel_years",
};

const isIterationInterval = (interval?: IIntervalOption) =>
  Boolean(interval && ITERATION_LABEL_KEYS[interval.type]);

interface IActionSectionProps {
  id: number;
  hospitalId?: number;
  intervalOptions: IIntervalOption[];
  typeOptions: IOption[];
  actionOptions: IOptionGroup[];
  longDateFormat?: string;
  isCloseButtonVisible: boolean;
  isSectionActive: boolean;
  disableNotEditableFields?: boolean;
  isLoadingOptions?: boolean;
  onRemove(id: number): void;
  handleActiveSection(id: number): void;
}

interface IActionFormikSectionProps extends IActionSectionProps {
  content?: IProtocolActions;
}

interface IActionSectionChildProps extends IActionSectionProps {
  formik: FormikProps<IProtocolActions>;
}

function ActionSectionChild({
  formik,
  id,
  hospitalId,
  intervalOptions,
  typeOptions,
  actionOptions,
  longDateFormat,
  isCloseButtonVisible,
  isSectionActive,
  disableNotEditableFields,
  isLoadingOptions,
  onRemove,
  handleActiveSection,
}: IActionSectionChildProps) {
  const { t } = useTranslation();
  const [isMounted, setIsMounted] = useState(false);

  const [intervalIterationLabel, setIntervalIterationLabel] = useState<string>(
    t("protocols_action_defaultIterationLabel")
  );

  useEffect(() => {
    setIsMounted(true);

    return () => {
      setIsMounted(false);
    };
  }, []);

  // EN-5191: matched on the API's ActionIntervalType, not on the display name. The name is
  // server-supplied copy and will be translated; these branches decide which frequencies are
  // offered, whether the end date is locked and whether the iteration field applies, so they
  // have to key off the enum.
  const pinToHomeScreenOption = intervalOptions.find(
    (x) => x.type === "PinToAppHomeScreen"
  );

  const onceOffIntervalOption = intervalOptions.find(
    (x) => x.type === "OnceOff"
  );

  const minDate =
    formik.values.actionIntervalId === onceOffIntervalOption?.value
      ? dayjs().add(-1, "day")
      : undefined;

  const shouldShowErrorMessage = (
    field:
      | "actionName"
      | "actionType"
      | "actionValue"
      | "intervalIterations"
      | "startDate"
      | "endDate"
      | "actionIntervalId"
  ) => {
    return formik?.touched[field] ? formik?.errors[field] ?? "" : "";
  };

  const typesDropdown = useMemo(() => {
    if (!isMounted) return null;

    return (
      <SelectDropdown
        placeholder={t("protocols_action_selectType")}
        className={selectCss()}
        width={248}
        height={42}
        value={formik?.values.actionType}
        onValueChange={(value) => {
          formik?.setFieldValue("actionType", value as string);
          formik?.setFieldValue("actionValue", undefined);
          formik?.setFieldValue("actionIntervalId", undefined);
        }}
        options={typeOptions}
        disabled={!disableNotEditableFields}
      />
    );
  }, [typeOptions, hospitalId, isMounted, t]);

  const isDropdownLoading =
    isLoadingOptions && formik?.values.actionType === "Measurement";

  const getActionTypeDisplayLabel = (actionType?: string) => {
    if (!actionType) return t("protocols_action_action");
    if (actionType === "ExerciseTest")
      return t("protocols_action_exerciseTest");
    if (actionType === "Measurement") return t("protocols_action_measurement");
    if (actionType === "Survey") return t("protocols_action_survey");
    return actionType;
  };

  const actionsDropdown = useMemo(() => {
    if (!isMounted) return null;

    const displayLabel = getActionTypeDisplayLabel(formik?.values.actionType);

    return (
      <>
        <p style={{ width: "110px" }}>{displayLabel} *</p>

        <SelectDropdown
          placeholder={
            isDropdownLoading
              ? t("protocols_action_loading")
              : t("protocols_action_select", {
                  what: displayLabel.toLowerCase(),
                })
          }
          className={selectCss()}
          width={248}
          height={42}
          showSearch
          optionFilterProp="label"
          value={formik?.values.actionValue}
          onValueChange={(value) => {
            formik?.setFieldValue("actionValue", value as string);

            let actionLabel = "";

            actionOptions.forEach((group) => {
              const label = group.options.find((x) => x.value === value)?.label;

              if (label) {
                actionLabel = label;
              }
            });

            if (actionLabel !== "") {
              formik?.setFieldValue("actionName", actionLabel);
            }
          }}
          options={
            actionOptions.find(
              formik?.values.actionType === "Survey"
                ? (x) =>
                    x.label === `${formik?.values.actionType} ${hospitalId}`
                : (x) => x.label === formik?.values.actionType
            )?.options ?? []
          }
          disabled={!disableNotEditableFields || isDropdownLoading}
        />
      </>
    );
  }, [
    typeOptions,
    formik?.values.actionType,
    hospitalId,
    isMounted,
    actionOptions,
    isDropdownLoading,
    t,
  ]);

  const intervalDropdown = useMemo(() => {
    if (!isMounted) return null;

    const options =
      formik?.values.actionType === "Survey"
        ? intervalOptions
        : intervalOptions.filter(
            (x) => x.value !== pinToHomeScreenOption?.value
          );

    return (
      <SelectDropdown
        placeholder={t("protocols_action_selectFrequency")}
        className={selectCss()}
        width={248}
        height={42}
        value={formik?.values.actionIntervalId}
        onValueChange={(value) => {
          formik?.setFieldValue("actionIntervalId", value as string);

          const intervalOption = options.find((i) => i.value === value);

          if (intervalOption) {
            const iterationLabelKey = ITERATION_LABEL_KEYS[intervalOption.type];

            if (iterationLabelKey) {
              setIntervalIterationLabel(t(iterationLabelKey));
            }

            if (intervalOption.value === onceOffIntervalOption?.value) {
              // Get start of today in user's local timezone
              // This ensures consistent behavior regardless of timezone
              const today = dayjs().startOf("day");

              if (
                formik?.values?.startDate != null &&
                formik.values.startDate.isBefore(today, "day")
              ) {
                // Reset to start of today if date is in the past
                formik?.setFieldValue("startDate", today);
              }

              formik?.setFieldValue("endDate", null);
            }
          }
        }}
        options={options}
      />
    );
  }, [formik?.values.actionType, intervalOptions, hospitalId, isMounted, t]);

  return (
    <Section isInactive={!isSectionActive}>
      {isSectionActive ? <ActiveBar /> : null}

      {isCloseButtonVisible ? (
        <CloseContainer onClick={() => onRemove(id)}>
          <Close />
        </CloseContainer>
      ) : null}

      <SectionItem onClick={() => handleActiveSection(id)}>
        <p style={{ width: "110px" }}>{t("protocols_action_type")} *</p>
        {typesDropdown}
        <ErrorMessage>{shouldShowErrorMessage("actionType")}</ErrorMessage>
      </SectionItem>

      <SectionItem onClick={() => handleActiveSection(id)}>
        {actionsDropdown}
        <ErrorMessage>{shouldShowErrorMessage("actionValue")}</ErrorMessage>
      </SectionItem>

      <SectionItem onClick={() => handleActiveSection(id)}>
        <p style={{ width: "110px" }}>{t("protocols_action_frequency")} *</p>
        {intervalDropdown}
        <ErrorMessage>
          {shouldShowErrorMessage("actionIntervalId")}
        </ErrorMessage>
      </SectionItem>

      {isIterationInterval(
        intervalOptions.find((i) => i.value === formik.values.actionIntervalId)
      ) ? (
        <SectionItem onClick={() => handleActiveSection(id)}>
          <p style={{ width: "110px" }}>{intervalIterationLabel} *</p>
          <TextField
            label=""
            type="number"
            placeholder={intervalIterationLabel}
            name="intervalIterations"
            value={formik.values.intervalIterations}
            onChange={(event) => {
              formik.setFieldValue("intervalIterations", event.target.value);
            }}
            className={textFieldClassName()}
            backgroudColor="white"
            errorMessage={shouldShowErrorMessage("intervalIterations")}
          />
        </SectionItem>
      ) : null}

      <SectionItem onClick={() => handleActiveSection(id)}>
        <p style={{ width: "110px" }}>{t("protocols_action_startDate")} *</p>
        <DateTimePickerInput
          value={formik.values.startDate}
          format={longDateFormat}
          onChange={(value: Dayjs) => formik.setFieldValue("startDate", value)}
          width="248px"
          hideNowButton
          minuteStep={15}
          disabled={!disableNotEditableFields}
          minDate={minDate}
          defaultTime={dayjs().hour(9).minute(0).second(0)}
        />

        <ErrorMessage>{shouldShowErrorMessage("startDate")}</ErrorMessage>
      </SectionItem>

      <SectionItem onClick={() => handleActiveSection(id)}>
        <p style={{ width: "110px" }}>{t("protocols_action_endDate")}</p>
        <DateTimePickerInput
          placeholder={t("protocols_action_selectDateOptional")}
          value={formik.values.endDate}
          format={longDateFormat}
          onChange={(value: Dayjs) => formik.setFieldValue("endDate", value)}
          width="248px"
          hideNowButton
          minuteStep={15}
          disabled={
            formik.values.actionIntervalId === onceOffIntervalOption?.value
          }
          defaultTime={dayjs().hour(9).minute(0).second(0)}
        />

        <ErrorMessage>{shouldShowErrorMessage("endDate")}</ErrorMessage>
      </SectionItem>
    </Section>
  );
}

export const ActionSection = forwardRef<
  FormikProps<IProtocolActions>,
  IActionFormikSectionProps
>(
  (
    {
      id,
      hospitalId,
      intervalOptions,
      typeOptions,
      actionOptions,
      longDateFormat,
      isCloseButtonVisible,
      isSectionActive,
      content,
      disableNotEditableFields,
      isLoadingOptions,
      onRemove,
      handleActiveSection,
    },
    ref
  ) => {
    const { t } = useTranslation();

    const handleOnSubmitForm = () => {};

    const formValidationSchema = yup.object().shape({
      actionType: yup.string().required(t("protocols_action_typeRequired")),
      actionValue: yup.string().required(t("protocols_action_valueRequired")),
      intervalIterations: yup.number().when("actionIntervalId", {
        is: (actionIntervalId: string) =>
          isIterationInterval(
            intervalOptions.find((i) => i.value === actionIntervalId)
          ),
        then: yup
          .number()
          .min(1, t("protocols_action_iterationMin"))
          .required(t("protocols_action_iterationRequired")),
        otherwise: yup.number(),
      }),
      startDate: yup
        .string()
        .required(t("protocols_action_startDateRequired"))
        .nullable(),
      actionIntervalId: yup
        .string()
        .required(t("protocols_action_frequencyRequired")),
    });

    return (
      <Formik<IProtocolActions>
        innerRef={ref}
        initialValues={{
          id: content?.id,
          actionName: content?.actionName,
          actionType: content?.actionType,
          actionValue: content?.actionValue,
          intervalIterations: content?.intervalIterations ?? 1,
          startDate: content?.startDate ? dayjs(content?.startDate) : undefined,
          endDate: content?.endDate ? dayjs(content?.endDate) : undefined,
          actionIntervalId: content?.actionIntervalId,
        }}
        onSubmit={handleOnSubmitForm}
        validationSchema={formValidationSchema}
        enableReinitialize
      >
        {(formik) => (
          <Form>
            <ActionSectionChild
              formik={formik}
              id={id}
              hospitalId={hospitalId}
              intervalOptions={intervalOptions}
              typeOptions={typeOptions}
              actionOptions={actionOptions}
              longDateFormat={longDateFormat}
              isCloseButtonVisible={isCloseButtonVisible}
              isSectionActive={isSectionActive}
              disableNotEditableFields={disableNotEditableFields}
              isLoadingOptions={isLoadingOptions}
              onRemove={onRemove}
              handleActiveSection={handleActiveSection}
            />
          </Form>
        )}
      </Formik>
    );
  }
);
